import React, { useState } from 'react';
import { acceptLegalTerms } from '../lib/functions';
import { LogoIcon } from './Logo';
import { CURRENT_LEGAL_VERSION, TermsOfService, DataProcessingAgreement } from './legal/LegalContent';

interface Props {
  schoolId: string;
  schoolAdminId: string;
  schoolAdminName: string;
  /** Called immediately after the Firestore write succeeds — closes the gate */
  onAccepted: () => void;
}

type Tab = 'tos' | 'dpa';

function PrintIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
  );
}

export default function LegalGate({ schoolId, schoolAdminId, schoolAdminName, onAccepted }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('tos');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bothAccepted = tosAccepted && dpaAccepted;

  const handlePrint = (doc: Tab) => {
    const url = `/legal/${doc}?print=1`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleAccept = async () => {
    if (!bothAccepted) return;
    setSaving(true);
    setError(null);
    try {
      await acceptLegalTerms({ schoolId, version: CURRENT_LEGAL_VERSION });
      onAccepted();
    } catch (e) {
      console.error('[LegalGate] acceptLegalTerms FAILED:', e);
      setError('Failed to record acceptance. Please try again or contact hello@pmreview.co.uk.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <LogoIcon variant="bars" size={36} theme="dark" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Before you continue</h1>
            <p className="text-sm text-gray-500">
              Please read and accept the Terms of Service and Data Processing Agreement to activate your school account.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {([
            { key: 'tos' as Tab, label: 'Terms of Service', done: tosAccepted },
            { key: 'dpa' as Tab, label: 'Data Processing Agreement', done: dpaAccepted },
          ]).map(({ key, label, done }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === key
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {done && (
                <svg className="h-4 w-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {label}
            </button>
          ))}
        </div>

        {/* Document content */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          {/* Document toolbar */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50 rounded-t-2xl">
            <span className="text-xs text-gray-500">
              {activeTab === 'tos' ? 'Terms of Service' : 'Data Processing Agreement'} · Version {CURRENT_LEGAL_VERSION}
            </span>
            <button
              type="button"
              onClick={() => handlePrint(activeTab)}
              className="flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-800 font-medium"
            >
              <PrintIcon />
              Print / Save as PDF
            </button>
          </div>

          <div className="px-8 py-8 max-h-[52vh] overflow-y-auto">
            {activeTab === 'tos' ? <TermsOfService /> : <DataProcessingAgreement />}
          </div>

          {/* Acceptance checkbox */}
          <div className="border-t border-gray-100 px-8 py-5">
            {activeTab === 'tos' ? (
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                />
                <span className="text-sm text-gray-700">
                  I confirm that I have read the <strong>Terms of Service</strong> and accept them on behalf of my school.
                  {' '}I have authority to enter into agreements on behalf of my school.
                </span>
              </label>
            ) : (
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  checked={dpaAccepted}
                  onChange={(e) => setDpaAccepted(e.target.checked)}
                />
                <span className="text-sm text-gray-700">
                  I confirm that I have read the <strong>Data Processing Agreement</strong> and accept it on behalf of my school as the data controller.
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Navigation + accept */}
        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-gray-400">
            Both documents must be accepted before you can access PM Review.
          </div>
          <div className="flex items-center gap-3">
            {activeTab === 'tos' && (
              <button
                type="button"
                onClick={() => setActiveTab('dpa')}
                className="btn-secondary"
              >
                Next: Data Processing Agreement →
              </button>
            )}
            {activeTab === 'dpa' && (
              <button
                type="button"
                onClick={() => setActiveTab('tos')}
                className="btn-secondary text-sm"
              >
                ← Back
              </button>
            )}
            {activeTab === 'dpa' && (
              <button
                type="button"
                disabled={!bothAccepted || saving}
                onClick={() => void handleAccept()}
                className="btn-primary disabled:opacity-50"
                title={!bothAccepted ? 'Please accept both documents first' : undefined}
              >
                {saving ? 'Saving…' : 'Accept both and continue →'}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-gray-400">
          Questions? Contact{' '}
          <a href="mailto:hello@pmreview.co.uk" className="underline hover:text-gray-600">
            hello@pmreview.co.uk
          </a>
        </p>
      </div>
    </div>
  );
}
